import axios from "axios";
import { siteConfig, storageKey } from "./siteConfig";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_BASE });
const DEVICE_ID_KEY = storageKey("device_id");
const CORE_FAILURE_WINDOW_MS = 30000;
const CORE_FAILURE_THRESHOLD = 2;
let coreFailureCount = 0;
let firstCoreFailureAt = 0;

function hashString(value) {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

function cookieValue(name) {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
}

function setDeviceCookie(value) {
  if (typeof document === "undefined" || !value) return;
  document.cookie = `${DEVICE_ID_KEY}=${encodeURIComponent(value)}; Max-Age=31536000; Path=/; SameSite=Lax`;
}

function randomDeviceId() {
  const cryptoApi = typeof crypto !== "undefined" ? crypto : null;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  const bytes = new Uint8Array(16);
  if (cryptoApi?.getRandomValues) {
    cryptoApi.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  }
  return `dev-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getDeviceId() {
  if (typeof window === "undefined") return "";
  let value = "";
  try {
    value = window.localStorage.getItem(DEVICE_ID_KEY) || "";
    if (!value) {
      value = cookieValue(DEVICE_ID_KEY) || randomDeviceId();
      window.localStorage.setItem(DEVICE_ID_KEY, value);
    }
  } catch {
    value = cookieValue(DEVICE_ID_KEY) || randomDeviceId();
  }
  setDeviceCookie(value);
  return value;
}

function getDeviceFingerprint() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return "";
  const screenInfo = typeof screen === "undefined" ? {} : screen;
  const timezone = Intl.DateTimeFormat?.().resolvedOptions?.().timeZone || "";
  const parts = [
    navigator.userAgent,
    navigator.platform,
    navigator.language,
    Array.isArray(navigator.languages) ? navigator.languages.join(",") : "",
    timezone,
    new Date().getTimezoneOffset(),
    screenInfo.width,
    screenInfo.height,
    screenInfo.colorDepth,
    window.devicePixelRatio,
    navigator.hardwareConcurrency,
    navigator.deviceMemory,
    navigator.maxTouchPoints,
  ];
  return hashString(parts.join("|"));
}

function getCanvasSignature() {
  if (typeof document === "undefined") return "";
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 240;
    canvas.height = 60;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";
    ctx.textBaseline = "top";
    ctx.font = "16px Arial";
    ctx.fillStyle = "#f3c13a";
    ctx.fillRect(0, 0, 240, 60);
    ctx.fillStyle = "#102015";
    ctx.fillText(`${siteConfig.name} device check 17`, 8, 8);
    ctx.strokeStyle = "#7c3aed";
    ctx.strokeRect(4, 4, 180, 42);
    return hashString(canvas.toDataURL());
  } catch {
    return "";
  }
}

function getWebglSignature() {
  if (typeof document === "undefined") return "";
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl) return "";
    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
    const vendor = debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR);
    const renderer = debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER);
    return hashString([vendor, renderer].join("|"));
  } catch {
    return "";
  }
}

function getDeviceSignature() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return "";
  const screenInfo = typeof screen === "undefined" ? {} : screen;
  const timezone = Intl.DateTimeFormat?.().resolvedOptions?.().timeZone || "";
  const parts = [
    navigator.platform,
    navigator.language,
    Array.isArray(navigator.languages) ? navigator.languages.join(",") : "",
    timezone,
    new Date().getTimezoneOffset(),
    screenInfo.width,
    screenInfo.height,
    screenInfo.availWidth,
    screenInfo.availHeight,
    screenInfo.colorDepth,
    window.devicePixelRatio,
    navigator.hardwareConcurrency,
    navigator.deviceMemory,
    navigator.maxTouchPoints,
    getCanvasSignature(),
    getWebglSignature(),
  ];
  return hashString(parts.join("|"));
}

function isCoreReadRequest(config) {
  const method = String(config?.method || "get").toLowerCase();
  const url = String(config?.url || "");
  return (
    method === "get" &&
    (
      url === "/games" ||
      url.startsWith("/games?") ||
      url.startsWith("/games/") ||
      url === "/leaderboard" ||
      url === "/auth/me"
    )
  );
}

function clearCoreFailures(response) {
  if (isCoreReadRequest(response?.config)) {
    coreFailureCount = 0;
    firstCoreFailureAt = 0;
  }
}

function notifyMaintenance(err) {
  if (typeof window === "undefined") return;
  if (window.location.pathname.startsWith("/admin")) return;
  if (axios.isCancel?.(err)) return;
  if (err?.config?.skipMaintenance) return;

  const status = err?.response?.status || 0;
  const coreReadUrl = isCoreReadRequest(err?.config);
  const coreReadFailure = coreReadUrl && (!err?.response || status >= 500);
  if (!coreReadFailure) return;

  const now = Date.now();
  if (!firstCoreFailureAt || now - firstCoreFailureAt > CORE_FAILURE_WINDOW_MS) {
    firstCoreFailureAt = now;
    coreFailureCount = 0;
  }
  coreFailureCount += 1;
  if (coreFailureCount < CORE_FAILURE_THRESHOLD) return;

  window.dispatchEvent(
    new CustomEvent("sophia:maintenance", {
      detail: {
        source: "api",
        status,
        fatal: false,
      },
    }),
  );
}

function handleAdminAuthExpired(err) {
  if (typeof window === "undefined") return;
  if (err?.response?.status !== 401) return;
  const url = String(err?.config?.url || "");
  const path = window.location.pathname;
  const adminRequest =
    url.startsWith("/admin") ||
    (path.startsWith("/admin") && ["/auth/me", "/auth/reset-password"].includes(url));
  if (!adminRequest || url === "/auth/admin/login") return;
  localStorage.removeItem("aurum_admin_token");
  localStorage.removeItem("aurum_token");
  if (path !== "/admin") window.location.assign("/admin");
}

function handlePlayerAuthExpired(err) {
  if (typeof window === "undefined") return;
  if (err?.response?.status !== 401) return;
  const url = String(err?.config?.url || "");
  const path = window.location.pathname;
  if (path.startsWith("/admin") || url.startsWith("/admin") || url.startsWith(`${API_BASE}/admin`)) return;
  localStorage.removeItem("aurum_token");
  window.dispatchEvent(new CustomEvent("sophia:auth-expired"));
}

api.interceptors.request.use((config) => {
  const url = String(config.url || "");
  const adminContext =
    url.startsWith("/admin") ||
    url.startsWith(`${API_BASE}/admin`) ||
    (typeof window !== "undefined" && window.location.pathname.startsWith("/admin"));
  const token = adminContext
    ? localStorage.getItem("aurum_admin_token") || localStorage.getItem("aurum_token")
    : localStorage.getItem("aurum_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const deviceId = getDeviceId();
  const fingerprint = getDeviceFingerprint();
  const signature = getDeviceSignature();
  if (deviceId) config.headers["X-Device-Id"] = deviceId;
  if (fingerprint) config.headers["X-Device-Fingerprint"] = fingerprint;
  if (signature) config.headers["X-Device-Signature"] = signature;
  return config;
});

api.interceptors.response.use(
  (response) => {
    clearCoreFailures(response);
    return response;
  },
  (err) => {
    handleAdminAuthExpired(err);
    handlePlayerAuthExpired(err);
    notifyMaintenance(err);
    return Promise.reject(err);
  },
);

export function isTemporaryProviderError(err) {
  const fallbackText = [
    err?.response?.data,
    err?.response?.data?.detail,
    err?.message,
  ].map((value) => (typeof value === "string" ? value : value ? JSON.stringify(value) : "")).join(" ");
  const lower = fallbackText.toLowerCase();
  return (
    lower.includes("origin web server returned") ||
    lower.includes("temporary cloudflare") ||
    lower.includes("temporary origin error") ||
    lower.includes("provider server returned a temporary")
  );
}

export function formatApiError(err, options = {}) {
  if (err?.response?.status === 401) {
    return "Please sign in again.";
  }
  const providerDetail = err?.response?.data?.detail;
  if (options.audience === "player" && providerDetail?.error_kind === "insufficient_provider_credit") {
    return "This game has insufficient operator credits for your load including bonus. Your wallet has not been charged. Please contact support.";
  }
  if (isProviderMaintenanceError(err)) {
    return "The game provider is under maintenance. Please wait before trying again.";
  }
  if (isTemporaryProviderError(err)) {
    if (options.audience === "player") {
      return "Game provider is temporarily unavailable. Please try again in a moment.";
    }
    return "Provider server is temporarily unavailable. Retry after a moment; if credentials are correct, save without live test and test again later.";
  }
  const d = err?.response?.data?.detail;
  if (d == null) return err?.message || "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    const messages = d.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).filter(Boolean);
    if (messages.some((message) => message.toLowerCase().includes("field required"))) {
      return "Please fill every required field and upload ID front, ID back, and selfie.";
    }
    return [...new Set(messages)].join(" ");
  }
  if (d?.msg) return d.msg;
  if (d?.message && d?.response?.error) return `${d.message}: ${d.response.error}`;
  if (d?.message && d?.response?.message) return `${d.message}: ${d.response.message}`;
  if (d?.message) return d.message;
  if (d?.response?.error) {
    const details = d.response.details ? ` ${JSON.stringify(d.response.details)}` : "";
    return `${d.response.error}${details}`;
  }
  if (d?.error) {
    const details = d.details ? ` ${JSON.stringify(d.details)}` : "";
    return `${d.error}${details}`;
  }
  return JSON.stringify(d);
}

export function isProviderMaintenanceError(err) {
  const detail = err?.response?.data?.detail;
  if (detail?.provider_submission_uncertain || detail?.pending_provider_verification) return false;
  const text = (typeof detail === "string" ? detail : JSON.stringify(detail || {})).toLowerCase();
  return /maintenance|维护|維護/.test(text);
}

export default api;
